import { defineStore } from 'pinia';
import type { SelectedFilter } from './types/selected-filter.type';
import type { SelectedFilterType } from './types/selected-filter-type.type';
import type { SearchMode } from './types/search-mode.type';
import type { JobLocationCoordinates } from '@/core/shared/job/job.persistence';
import type { SearchGatedUserProfile } from './types/search-gated-user-profile.type';
import { clone } from '@/utils/objects/clone.util';
import type { DefaultPagination } from './types/default-pagination.type';
import { computed, ref } from 'vue';
import { defaultSearchQuery } from './consts/default-seach-query.const';
import { defaultPagination } from './consts/default-pagination.const';
import { arrayToRecord } from '@/utils/objects/array-to-record.util';
import { SearchTab } from './types/search-tab.enum';

export const useSearchStore = defineStore('sourcing-search', () => {
  const activeTab = ref<SearchTab>(SearchTab.MATCHES);
  const candidates = ref<SearchGatedUserProfile[]>([]);
  const serverItems = ref<number>();
  const paginationItemsLength = ref<number>();
  const totalPages = ref<number>();
  const scrollLevel = ref<number>(0);
  const searchQuery = ref<typeof defaultSearchQuery>(clone(defaultSearchQuery));
  const selectedFilters = ref<SelectedFilter[]>([]);
  const selectedFiltersId = ref<number>();
  // Job-mode ("search from a job") request params; the shared search service sets these
  // from the active job so the request runs as a document search.
  const searchMode = ref<SearchMode>();
  const jobId = ref<number>();
  const queryMode = ref<'keyword' | 'document'>();
  const documentSearchJobTitle = ref<string>();
  // Coordinates fence the job-mode geo filter (deterministic, ONE-6241). The human-readable
  // location label rides on the LOCATION filter chip (sent as locationText), not a store field.
  const jobLocationCoordinates = ref<JobLocationCoordinates>();
  // Rerun attribution forwarded from the shared store at sync time; sent on the candidates POST.
  const source = ref<'typed' | 'recent'>('typed');
  const parentSessionId = ref<string | null>(null);
  const searchSessionId = ref<string | undefined>();
  // Inferred filter types the recruiter removed for the current query; forwarded at sync
  // time so natural-mode extraction doesn't re-apply them on a re-run (ONE-6178).
  const suppressInferredTypes = ref<SelectedFilterType[]>();
  const locationSearchInput = ref<string>('');
  const pagination = ref<DefaultPagination>(clone(defaultPagination));
  const candidateIds = ref<number[]>([]);
  const selectedFiltersRecord = computed(() => arrayToRecord(selectedFilters.value, 'type'));
  const displayedCandidates = computed(() => {
    if (activeTab.value === SearchTab.SKIPPED) {
      return candidates.value.filter((c) => c.skipped);
    }
    return candidates.value.filter((c) => !c.skipped);
  });

  // alphabetized list
  return {
    activeTab,
    candidateIds,
    candidates,
    displayedCandidates,
    documentSearchJobTitle,
    jobId,
    jobLocationCoordinates,
    locationSearchInput,
    pagination,
    paginationItemsLength,
    parentSessionId,
    queryMode,
    scrollLevel,
    searchMode,
    searchQuery,
    selectedFilters,
    selectedFiltersId,
    selectedFiltersRecord,
    searchSessionId,
    serverItems,
    source,
    suppressInferredTypes,
    totalPages,
  };
});
